import { CaretDownIcon, Globe } from '@phosphor-icons/react';
import { ReactNode } from 'react';

import { cn } from '../../../../helpers/classBuilders';
import { Maybe, ValueLabelType } from '../../../../types/common';
import { Loading } from '../../../Loading';
import { Popover } from '../../../Popover';
import { Skeleton } from '../../../Skeleton';
import { Stack } from '../../../Stack';
import { Tooltip } from '../../../Tooltip';
import { Paragraph } from '../../../Typography';

interface Props {
  id: string;
  selected?: Maybe<ValueLabelType>;
  options: ValueLabelType[];
  onOptionSelect?: (option: ValueLabelType) => void;
  isLoading?: boolean;
  charLength?: number; // Set max number of characters in header
  width?: number;
  /**
   * Trigger style. `default` is the compact gray pill. `secondary` mirrors the
   * Button `color="secondary"` look (outlined, brand-secondary tokens) for use
   * as an action menu — pair it with `triggerLabel`.
   */
  variant?: 'default' | 'secondary';
  /**
   * Fixed label shown in the trigger instead of the selected option. Use it for
   * action menus (e.g. an "Export" button) where there is no selected value.
   */
  triggerLabel?: ReactNode;
  /** Disables the trigger so the menu can't be opened (e.g. while data loads). */
  disabled?: boolean;
  settings?: {
    icon: ReactNode;
    hideDefaultIcon?: boolean;
  };
  isLoadingChange?: boolean;
}

export const DropdownMenu = ({
  id,
  selected,
  options,
  isLoading = false,
  settings = {
    icon: <Globe size={24} />,
    hideDefaultIcon: false,
  },
  onOptionSelect,
  isLoadingChange,
  charLength = Infinity,
  width,
  variant = 'default',
  triggerLabel,
  disabled = false,
}: Props) => {
  const itemId = `${id}-dropdown-menu`;
  const widthStyle = {
    ...(width && { minWidth: `${width}px`, maxWidth: `${width}px` }),
  };

  // The secondary variant reuses the Button secondary color tokens so it stays
  // visually identical to a `<Button color="secondary">` and re-themes with it.
  const triggerClassName = cn(
    variant === 'secondary'
      ? cn(
          'inline-flex items-center gap-2 cursor-pointer h-10 px-4 rounded-lg border-2 bg-transparent transition-colors',
          'border-[var(--color-button-secondary-default)] text-[var(--color-button-secondary-default)]',
          'hover:bg-[var(--color-button-secondary-default)] hover:text-white',
          'focus-visible:bg-[var(--color-button-secondary-default)] focus-visible:text-white focus-visible:focus-ring-form',
        )
      : 'flex items-center gap-2 cursor-pointer h-10 p-1 rounded-lg bg-gray-100',
    { 'opacity-60 pointer-events-none cursor-not-allowed': disabled },
  );

  const renderValue = () => {
    if (selected) {
      const len = selected.label.length;
      if (len > charLength) {
        return (
          <Tooltip
            align="bottom"
            content={<div className="max-w-[200px]">{selected.label}</div>}
            trigger={
              <span
                className={cn('text-xs font-medium', {
                  'text-gray-500': isLoadingChange,
                })}
              >
                {selected.label.slice(0, charLength) + '...'}
              </span>
            }
          />
        );
      } else {
        return (
          <span
            className={cn('text-xs font-medium', {
              'text-gray-500': isLoadingChange,
            })}
          >
            {selected.label}
          </span>
        );
      }
    }

    return (
      <span
        className={cn('text-xs font-medium', {
          'text-gray-500': isLoadingChange,
        })}
      >
        Select Option
      </span>
    );
  };

  const renderIcon = () => {
    if (isLoadingChange) {
      return <Loading size={12} />;
    }

    if (settings.icon && !settings.hideDefaultIcon) {
      return settings.icon;
    }

    return null;
  };

  const renderLabel = () => {
    if (triggerLabel !== undefined) {
      return (
        <span className={cn('font-medium text-base')}>{triggerLabel}</span>
      );
    }

    return renderValue();
  };

  if (isLoading) {
    return (
      <Stack direction="row" align="center" gap={1}>
        <Skeleton width={24} height={24} />
        <Skeleton width={80} height={20} />
      </Stack>
    );
  }

  return (
    <Popover
      id={itemId}
      disabled={disabled}
      defaultPosition="left"
      trigger={() => (
        <div
          data-testid="dropdown-menu-trigger"
          style={widthStyle}
          className={triggerClassName}
        >
          <span className="min-w-1">{renderIcon()}</span>
          {renderLabel()}
          <span
            className={cn('flex items-center', {
              'ml-auto': variant !== 'secondary',
            })}
            aria-hidden
          >
            <CaretDownIcon size={16} />
          </span>
        </div>
      )}
      classNames={{ content: 'overflow-hidden' }}
      content={({ handleClose }) => (
        <div
          className={cn('max-h-40 overflow-y-auto', {
            // Action menus size to their content so each option stays on one
            // line instead of wrapping to the trigger width.
            'w-max': variant === 'secondary',
          })}
          style={widthStyle}
        >
          {options.map(option => {
            const isSelected = selected?.value === option.value;

            return (
              <div
                role="menuitem"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOptionSelect?.(option);
                  }
                }}
                key={option.value}
                className={cn(
                  'p-2 hover:bg-gray-100 focus-visible:focus-ring-form-inset cursor-pointer',
                  {
                    'bg-gray-100 font-semibold': isSelected,
                  },
                )}
                onClick={() => {
                  if (isSelected) {
                    return;
                  }

                  if (isLoadingChange) {
                    return;
                  }

                  onOptionSelect?.(option);
                  handleClose();
                }}
              >
                <Paragraph
                  title={option.label}
                  className={cn({
                    'whitespace-nowrap': variant === 'secondary',
                    'line-clamp-2 hover:line-clamp-none':
                      variant !== 'secondary',
                  })}
                  size="sm"
                >
                  {option.label}
                </Paragraph>
              </div>
            );
          })}
        </div>
      )}
    />
  );
};
